/**
 * Výrazový jazyk pro programovatelné FILTROVÁNÍ, ŘAZENÍ a HLEDÁNÍ tras.
 *
 * Česky orientovaný jazyk (case-insensitive, diakritika volitelná) se ručně
 * psaným rekurzivním descent parserem — při parsování se nikdy nevolá eval.
 * Výstupem kompilace je ČITELNÝ JavaScript (jsSource), který se uživateli
 * ukáže při přepnutí do JS módu a dá se samostatně spustit přes
 * `new Function('return ' + jsSource)()`.
 *
 * Jádro je čistě výpočetní — žádná závislost na DOM ani na zbytku aplikace.
 */

export type ProgramKind = 'filter' | 'sort' | 'search';

export interface CompiledProgram {
  kind: ProgramKind;
  jsSource: string;
}

// ---------------------------------------------------------------------------
// Normalizace a pomocné funkce
// ---------------------------------------------------------------------------

/** Odstraní diakritiku a převede na malá písmena („Čekání" → „cekani"). */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** Levenshteinova editační vzdálenost (pro návrhy překlepů). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** Najde kandidáta se vzdáleností ≤ 2 (porovnává se bez diakritiky a case). */
function navrh(input: string, candidates: readonly string[]): string | undefined {
  const ni = normalize(input);
  let best: string | undefined;
  let bestD = 3; // hledáme jen vzdálenost ≤ 2
  for (const c of candidates) {
    const d = levenshtein(ni, normalize(c));
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

/** Chyba jazyka s pozicí (index znaku) v původním zdrojovém textu. */
class LangError extends Error {
  readonly pos: number;

  constructor(message: string, pos: number) {
    super(message);
    this.name = 'LangError';
    this.pos = pos;
  }
}

// ---------------------------------------------------------------------------
// Tabulka identifikátorů a funkcí
// ---------------------------------------------------------------------------

type ValueType = 'number' | 'boolean' | 'string';

interface IdentDef {
  /** Kanonické jméno (zobrazuje se v dokumentaci a v návrzích). */
  name: string;
  type: ValueType;
  doc: string;
  /** Kontextová proměnná (čte se z `ctx`, ne z trasy). */
  ctx?: boolean;
  /** Vygeneruje JS přístup; `v` je jméno proměnné s trasou (t / a / b). */
  make: (v: string) => string;
}

const IDENT_DEFS: readonly IdentDef[] = [
  { name: 'cas', type: 'number', doc: 'Celkový čas cesty v minutách', make: (v) => `${v}.totalMin` },
  { name: 'cena', type: 'number', doc: 'Cena cesty v Kč', make: (v) => `${v}.cost` },
  { name: 'cekani', type: 'number', doc: 'Čekání v minutách', make: (v) => `${v}.waitMin` },
  { name: 'emise', type: 'number', doc: 'Emise CO₂ v gramech', make: (v) => `${v}.emissions` },
  { name: 'delka', type: 'number', doc: 'Délka trasy v kilometrech', make: (v) => `${v}.distanceKm` },
  { name: 'komfort', type: 'number', doc: 'Komfort 1–5', make: (v) => `${v}.comfort` },
  { name: 'prestupy', type: 'number', doc: 'Počet přestupů', make: (v) => `${v}.transfers` },
  { name: 'spolehlivost', type: 'number', doc: 'Spolehlivost 0–1', make: (v) => `${v}.reliability` },
  { name: 'nejistota', type: 'number', doc: 'Nejistota odhadu času v minutách', make: (v) => `${v}.uncertaintyMin` },
  { name: 'bezAuta', type: 'boolean', doc: 'Cesta se obejde bez auta (ano/ne)', make: (v) => `${v}.carFree` },
  { name: 'bezbarierove', type: 'boolean', doc: 'Bezbariérová trasa (ano/ne)', make: (v) => `${v}.accessible` },
  {
    name: 'jeMHD',
    type: 'boolean',
    doc: 'Obsahuje úsek veřejnou dopravou (ano/ne)',
    make: (v) => `${v}.segments.some((s) => s.kind === 'transit')`,
  },
  { name: 'rezim', type: 'string', doc: 'Identifikátor dopravního režimu (text)', make: (v) => `${v}.modeId` },
  { name: 'nazev', type: 'string', doc: 'Název trasy (text)', make: (v) => `${v}.name` },
  {
    name: 'odhad',
    type: 'boolean',
    doc: 'Hodnoty jsou jen hrubý odhad (ano/ne)',
    make: (v) => `(${v}.dataSource === 'estimate')`,
  },
  { name: 'hodina', type: 'number', doc: 'Hodina odjezdu 0–23', ctx: true, make: () => 'ctx.hour' },
  { name: 'den', type: 'number', doc: 'Den v týdnu (1=Po … 7=Ne)', ctx: true, make: () => 'ctx.day' },
  { name: 'vikend', type: 'boolean', doc: 'Odjezd o víkendu (ano/ne)', ctx: true, make: () => 'ctx.weekend' },
];

const IDENT_BY_NORM = new Map<string, IdentDef>(IDENT_DEFS.map((d) => [normalize(d.name), d]));

interface FnDef {
  name: string;
  js: string;
  minArgs: number;
  maxArgs: number;
  doc: string;
}

const FN_DEFS: readonly FnDef[] = [
  { name: 'min', js: 'Math.min', minArgs: 2, maxArgs: Infinity, doc: 'min(a, b, …) — nejmenší z hodnot' },
  { name: 'max', js: 'Math.max', minArgs: 2, maxArgs: Infinity, doc: 'max(a, b, …) — největší z hodnot' },
  { name: 'abs', js: 'Math.abs', minArgs: 1, maxArgs: 1, doc: 'abs(x) — absolutní hodnota' },
  { name: 'zaokrouhli', js: 'Math.round', minArgs: 1, maxArgs: 1, doc: 'zaokrouhli(x) — na celé číslo' },
];

const FN_BY_NORM = new Map<string, FnDef>(FN_DEFS.map((d) => [normalize(d.name), d]));

/** Klíčová slova (v normalizované podobě) — nesmí kolidovat s proměnnými. */
const KEYWORDS = new Set([
  'a', 'and', 'nebo', 'or', 'ne', 'not',
  'kdyz', 'pak', 'jinak',
  'podle', 'sestupne', 'vzestupne',
  'filtr', 'razeni', 'konec',
]);

/**
 * Tabulka identifikátorů pro vizuální skládačku a dokumentaci.
 * `js` ukazuje přístup nad proměnnou `t` (resp. `ctx` u kontextových).
 */
export const EXPR_IDENTIFIERS: Array<{
  name: string;
  js: string;
  doc: string;
  type: 'number' | 'boolean' | 'string';
  ctx?: boolean;
}> = IDENT_DEFS.map((d) => ({
  name: d.name,
  js: d.make('t'),
  doc: d.doc,
  type: d.type,
  ...(d.ctx ? { ctx: true } : {}),
}));

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type TokKind = 'num' | 'str' | 'ident' | 'op' | 'eof';

interface Tok {
  kind: TokKind;
  /** Původní text tokenu (pro chybové hlášky). */
  text: string;
  /** Normalizovaná podoba (identifikátory a operátory; `==` → `=`). */
  norm: string;
  num?: number;
  str?: string;
  /** Index prvního znaku v původním zdroji. */
  pos: number;
}

const TWO_CHAR_OPS = ['<=', '>=', '!=', '==', '&&', '||'] as const;
const ONE_CHAR_OPS = '<>=!+-*/%(),:;';

function isDigit(c: string): boolean {
  return c >= '0' && c <= '9';
}

function isIdentStart(c: string): boolean {
  return /[A-Za-z_\u00c0-\u024f]/.test(c);
}

function isIdentPart(c: string): boolean {
  return isIdentStart(c) || isDigit(c);
}

function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t' || c === '\r' || c === '\n') {
      i++;
      continue;
    }
    const pos = i;

    // Číslo: desetinný oddělovač je tečka nebo čárka (čárka jen pokud po ní
    // hned následuje číslice — jinak jde o oddělovač argumentů či klíčů).
    if (isDigit(c)) {
      let j = i + 1;
      while (j < src.length && isDigit(src[j])) j++;
      if ((src[j] === '.' || src[j] === ',') && j + 1 < src.length && isDigit(src[j + 1])) {
        j += 2;
        while (j < src.length && isDigit(src[j])) j++;
      }
      const text = src.slice(i, j);
      toks.push({ kind: 'num', text, norm: text, num: parseFloat(text.replace(',', '.')), pos });
      i = j;
      continue;
    }

    // Řetězec: "…", '…' i české „…".
    if (c === '"' || c === "'" || c === '„' || c === '“') {
      const closers = c === '"' ? ['"'] : c === "'" ? ["'"] : ['“', '”', '"'];
      let j = i + 1;
      while (j < src.length && !closers.includes(src[j]) && src[j] !== '\n') j++;
      if (j >= src.length || src[j] === '\n') {
        throw new LangError('Neukončený řetězec — chybí koncová uvozovka.', pos);
      }
      const value = src.slice(i + 1, j);
      toks.push({ kind: 'str', text: src.slice(i, j + 1), norm: '', str: value, pos });
      i = j + 1;
      continue;
    }

    if (isIdentStart(c)) {
      let j = i + 1;
      while (j < src.length && isIdentPart(src[j])) j++;
      const text = src.slice(i, j);
      toks.push({ kind: 'ident', text, norm: normalize(text), pos });
      i = j;
      continue;
    }

    const two = src.slice(i, i + 2) as (typeof TWO_CHAR_OPS)[number];
    if (TWO_CHAR_OPS.includes(two)) {
      toks.push({ kind: 'op', text: two, norm: two === '==' ? '=' : two, pos });
      i += 2;
      continue;
    }
    if (ONE_CHAR_OPS.includes(c)) {
      toks.push({ kind: 'op', text: c, norm: c, pos });
      i++;
      continue;
    }

    throw new LangError(`Neočekávaný znak ‚${c}'.`, pos);
  }
  toks.push({ kind: 'eof', text: '', norm: '', pos: src.length });
  return toks;
}

// ---------------------------------------------------------------------------
// AST
// ---------------------------------------------------------------------------

type BinOp = '||' | '&&' | '===' | '!==' | '<' | '<=' | '>' | '>=' | '+' | '-' | '*' | '/' | '%';

type Node =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'ident'; entry: IdentDef; pos: number }
  | { kind: 'call'; fn: FnDef; args: Node[] }
  | { kind: 'un'; op: '!' | '-'; arg: Node }
  | { kind: 'bin'; op: BinOp; left: Node; right: Node }
  | { kind: 'tern'; cond: Node; then: Node; els: Node };

interface SortKey {
  expr: Node;
  desc: boolean;
}

type SearchBlock = { type: 'filter'; expr: Node } | { type: 'sort'; keys: SortKey[] };

interface SearchBranch {
  cond: Node;
  blocks: SearchBlock[];
}

interface SearchProgram {
  branches: SearchBranch[];
  blocks: SearchBlock[];
}

/** Zavolá `cb` pro každý identifikátor ve výrazu (rekurzivně). */
function projdiIdentifikatory(n: Node, cb: (id: { entry: IdentDef; pos: number }) => void): void {
  switch (n.kind) {
    case 'ident':
      cb(n);
      break;
    case 'call':
      n.args.forEach((a) => projdiIdentifikatory(a, cb));
      break;
    case 'un':
      projdiIdentifikatory(n.arg, cb);
      break;
    case 'bin':
      projdiIdentifikatory(n.left, cb);
      projdiIdentifikatory(n.right, cb);
      break;
    case 'tern':
      projdiIdentifikatory(n.cond, cb);
      projdiIdentifikatory(n.then, cb);
      projdiIdentifikatory(n.els, cb);
      break;
    default:
      break;
  }
}

// ---------------------------------------------------------------------------
// Parser (rekurzivní descent, žádný eval)
// ---------------------------------------------------------------------------

class Parser {
  private readonly toks: Tok[];
  private i = 0;

  constructor(src: string) {
    this.toks = tokenize(src);
  }

  private peek(offset = 0): Tok {
    return this.toks[Math.min(this.i + offset, this.toks.length - 1)];
  }

  private next(): Tok {
    const t = this.peek();
    if (t.kind !== 'eof') this.i++;
    return t;
  }

  private isKw(t: Tok, ...kws: string[]): boolean {
    return t.kind === 'ident' && kws.includes(t.norm);
  }

  private isOp(t: Tok, ...ops: string[]): boolean {
    return t.kind === 'op' && ops.includes(t.norm);
  }

  private eatOp(...ops: string[]): boolean {
    if (this.isOp(this.peek(), ...ops)) {
      this.next();
      return true;
    }
    return false;
  }

  private expectKw(kw: string, msg: string): void {
    const t = this.peek();
    if (!this.isKw(t, kw)) throw new LangError(msg, t.pos);
    this.next();
  }

  private expectOp(op: string, msg: string): void {
    const t = this.peek();
    if (!this.isOp(t, op)) throw new LangError(msg, t.pos);
    this.next();
  }

  expectEof(): void {
    const t = this.peek();
    if (t.kind !== 'eof') {
      throw new LangError(`Neočekávaný token ‚${t.text}' — za koncem výrazu nesmí nic následovat.`, t.pos);
    }
  }

  // --- výrazy -------------------------------------------------------------

  parseExpr(): Node {
    if (this.isKw(this.peek(), 'kdyz')) return this.parseTernary();
    return this.parseOr();
  }

  /** Ternár: KDYZ podmínka PAK x JINAK y. */
  private parseTernary(): Node {
    this.next(); // KDYZ
    const cond = this.parseExpr();
    this.expectKw('pak', 'Za podmínkou KDYZ se očekává klíčové slovo PAK.');
    const then = this.parseExpr();
    this.expectKw('jinak', 'Větev PAK musí pokračovat klíčovým slovem JINAK.');
    const els = this.parseExpr();
    return { kind: 'tern', cond, then, els };
  }

  private parseOr(): Node {
    let left = this.parseAnd();
    while (this.isKw(this.peek(), 'nebo', 'or') || this.isOp(this.peek(), '||')) {
      this.next();
      left = { kind: 'bin', op: '||', left, right: this.parseAnd() };
    }
    return left;
  }

  private parseAnd(): Node {
    let left = this.parseNot();
    while (this.isKw(this.peek(), 'a', 'and') || this.isOp(this.peek(), '&&')) {
      this.next();
      left = { kind: 'bin', op: '&&', left, right: this.parseNot() };
    }
    return left;
  }

  private parseNot(): Node {
    if (this.isKw(this.peek(), 'ne', 'not') || this.isOp(this.peek(), '!')) {
      this.next();
      return { kind: 'un', op: '!', arg: this.parseNot() };
    }
    return this.parseCmp();
  }

  private parseCmp(): Node {
    const left = this.parseAdd();
    const t = this.peek();
    if (this.isOp(t, '<', '<=', '>', '>=', '=', '!=')) {
      this.next();
      const op: BinOp = t.norm === '=' ? '===' : t.norm === '!=' ? '!==' : (t.norm as BinOp);
      return { kind: 'bin', op, left, right: this.parseAdd() };
    }
    return left;
  }

  private parseAdd(): Node {
    let left = this.parseMul();
    while (this.isOp(this.peek(), '+', '-')) {
      const op = this.next().norm as BinOp;
      left = { kind: 'bin', op, left, right: this.parseMul() };
    }
    return left;
  }

  private parseMul(): Node {
    let left = this.parseUnary();
    while (this.isOp(this.peek(), '*', '/', '%')) {
      const op = this.next().norm as BinOp;
      left = { kind: 'bin', op, left, right: this.parseUnary() };
    }
    return left;
  }

  private parseUnary(): Node {
    if (this.eatOp('-')) return { kind: 'un', op: '-', arg: this.parseUnary() };
    if (this.eatOp('+')) return this.parseUnary(); // unární plus nic nemění
    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const t = this.peek();

    if (t.kind === 'num') {
      this.next();
      return { kind: 'num', value: t.num! };
    }
    if (t.kind === 'str') {
      this.next();
      return { kind: 'str', value: t.str! };
    }
    if (this.isOp(t, '(')) {
      this.next();
      const inner = this.parseExpr();
      this.expectOp(')', 'Chybí pravá závorka.');
      return inner;
    }
    if (t.kind === 'ident') {
      if (KEYWORDS.has(t.norm)) {
        if (t.norm === 'kdyz') return this.parseTernary();
        throw new LangError(`Neočekávané klíčové slovo ‚${t.text}'.`, t.pos);
      }
      const fn = FN_BY_NORM.get(t.norm);
      if (fn) {
        if (!this.isOp(this.peek(1), '(')) {
          throw new LangError(`Funkce ‚${fn.name}' se volá se závorkami, např. ${fn.doc.split(' — ')[0]}.`, t.pos);
        }
        return this.parseCall(fn, t);
      }
      const entry = IDENT_BY_NORM.get(t.norm);
      if (entry) {
        this.next();
        return { kind: 'ident', entry, pos: t.pos };
      }
      // Neznámé jméno — rozlišíme funkci (následuje závorka) a proměnnou.
      if (this.isOp(this.peek(1), '(')) {
        const tip = navrh(t.text, FN_DEFS.map((f) => f.name));
        throw new LangError(
          `Neznámá funkce ‚${t.text}'${tip ? ` — měli jste na mysli ‚${tip}'?` : '.'}`,
          t.pos,
        );
      }
      const tip = navrh(t.text, IDENT_DEFS.map((d) => d.name));
      throw new LangError(
        `Neznámá proměnná ‚${t.text}'${tip ? ` — měli jste na mysli ‚${tip}'?` : '.'}`,
        t.pos,
      );
    }
    if (t.kind === 'eof') {
      throw new LangError('Neočekávaný konec výrazu.', t.pos);
    }
    throw new LangError(`Neočekávaný token ‚${t.text}'.`, t.pos);
  }

  private parseCall(fn: FnDef, nameTok: Tok): Node {
    this.next(); // jméno funkce
    this.next(); // '('
    const args: Node[] = [];
    if (!this.isOp(this.peek(), ')')) {
      do {
        args.push(this.parseExpr());
      } while (this.eatOp(','));
    }
    this.expectOp(')', `Chybí pravá závorka ve volání funkce ‚${fn.name}'.`);
    if (args.length < fn.minArgs || args.length > fn.maxArgs) {
      const oc = fn.minArgs === fn.maxArgs
        ? `přesně ${fn.minArgs} argument${fn.minArgs === 1 ? '' : 'y'}`
        : `aspoň ${fn.minArgs} argumenty`;
      throw new LangError(`Funkce ‚${fn.name}' očekává ${oc}.`, nameTok.pos);
    }
    return { kind: 'call', fn, args };
  }

  // --- ŘAZENÍ ---------------------------------------------------------------

  parseSortProgram(): SortKey[] {
    this.expectKw('podle', 'Program řazení musí začínat klíčovým slovem PODLE.');
    const keys = this.parseSortKeys();
    this.expectEof();
    return keys;
  }

  parseSortKeys(): SortKey[] {
    const keys: SortKey[] = [];
    do {
      const expr = this.parseExpr();
      let desc = false;
      if (this.isKw(this.peek(), 'sestupne')) {
        this.next();
        desc = true;
      } else if (this.isKw(this.peek(), 'vzestupne')) {
        this.next();
      }
      keys.push({ expr, desc });
    } while (this.eatOp(','));
    return keys;
  }

  // --- HLEDÁNÍ ---------------------------------------------------------------

  parseSearchProgram(): SearchProgram {
    const prog: SearchProgram = { branches: [], blocks: [] };
    for (;;) {
      while (this.eatOp(';')) { /* oddělovače bloků */ }
      const t = this.peek();
      if (t.kind === 'eof') break;
      if (this.isKw(t, 'filtr') || this.isKw(t, 'razeni')) {
        prog.blocks.push(this.parseSearchBlock());
        continue;
      }
      if (this.isKw(t, 'kdyz')) {
        prog.branches.push(this.parseSearchBranch());
        continue;
      }
      throw new LangError(
        `Neočekávaný token ‚${t.text}' — program HLEDANI se skládá z bloků FILTR, RAZENI a KDYZ.`,
        t.pos,
      );
    }
    return prog;
  }

  private parseSearchBlock(): SearchBlock {
    const t = this.next();
    if (t.norm === 'filtr') {
      return { type: 'filter', expr: this.parseExpr() };
    }
    // RAZENI [PODLE] klíč [SESTUPNE], …
    if (this.isKw(this.peek(), 'podle')) this.next();
    return { type: 'sort', keys: this.parseSortKeys() };
  }

  private parseSearchBranch(): SearchBranch {
    const kdyz = this.next(); // KDYZ
    const cond = this.parseExpr();
    // V podmínce HLEDANI jsou dostupné jen kontextové proměnné.
    projdiIdentifikatory(cond, (id) => {
      if (!id.entry.ctx) {
        throw new LangError(
          `V podmínce KDYZ programu HLEDANI lze použít jen kontextové proměnné (hodina, den, vikend) — `
          + `proměnná ‚${id.entry.name}' se vztahuje k jednotlivé trase.`,
          id.pos,
        );
      }
    });
    this.expectOp(':', 'Za podmínkou KDYZ v programu HLEDANI patří dvojtečka.');
    const blocks: SearchBlock[] = [];
    for (;;) {
      while (this.eatOp(';')) { /* oddělovače bloků */ }
      const t = this.peek();
      if (this.isKw(t, 'konec')) {
        this.next();
        break;
      }
      if (this.isKw(t, 'filtr') || this.isKw(t, 'razeni')) {
        blocks.push(this.parseSearchBlock());
        continue;
      }
      if (t.kind === 'eof') {
        throw new LangError('Větev KDYZ musí být ukončena klíčovým slovem KONEC.', kdyz.pos);
      }
      if (this.isKw(t, 'kdyz')) {
        throw new LangError('Vnořené větve KDYZ nejsou v programu HLEDANI podporovány.', t.pos);
      }
      throw new LangError(`Neočekávaný token ‚${t.text}' — ve větvi KDYZ smí být jen bloky FILTR a RAZENI.`, t.pos);
    }
    return { cond, blocks };
  }
}

// ---------------------------------------------------------------------------
// Generování čitelného JavaScriptu
// ---------------------------------------------------------------------------

/** Priority operátorů pro minimální, ale správné uzávorkování výstupu. */
const JS_PREC: Record<BinOp, number> = {
  '||': 2,
  '&&': 3,
  '===': 4, '!==': 4, '<': 4, '<=': 4, '>': 4, '>=': 4,
  '+': 5, '-': 5,
  '*': 6, '/': 6, '%': 6,
};

/**
 * Vygeneruje JS výrazu; `v` je jméno proměnné s trasou (t / a / b),
 * `parent` priorita nadřazeného uzlu (uzávorkuje se jen, když je to nutné).
 */
function gen(n: Node, v: string, parent: number): string {
  switch (n.kind) {
    case 'num':
      return String(n.value);
    case 'str':
      return JSON.stringify(n.value);
    case 'ident':
      return n.entry.make(v);
    case 'call':
      return `${n.fn.js}(${n.args.map((a) => gen(a, v, 0)).join(', ')})`;
    case 'un': {
      let arg = gen(n.arg, v, 7);
      if (n.op === '-' && arg.startsWith('-')) arg = `(${arg})`; // zabrání „--x"
      const s = `${n.op}${arg}`;
      return parent > 7 ? `(${s})` : s;
    }
    case 'bin': {
      const p = JS_PREC[n.op];
      const s = `${gen(n.left, v, p)} ${n.op} ${gen(n.right, v, p + 1)}`;
      return p < parent ? `(${s})` : s;
    }
    case 'tern': {
      const s = `${gen(n.cond, v, 2)} ? ${gen(n.then, v, 1)} : ${gen(n.els, v, 1)}`;
      return parent > 1 ? `(${s})` : s;
    }
  }
}

/** Hrubá typová dedukce — řetězcové řadicí klíče se porovnávají localeCompare. */
function inferType(n: Node): ValueType | 'unknown' {
  switch (n.kind) {
    case 'num':
      return 'number';
    case 'str':
      return 'string';
    case 'ident':
      return n.entry.type;
    case 'call':
      return 'number';
    case 'un':
      return n.op === '!' ? 'boolean' : 'number';
    case 'bin':
      return n.op === '&&' || n.op === '||' || JS_PREC[n.op] === 4 ? 'boolean' : 'number';
    case 'tern': {
      const a = inferType(n.then);
      const b = inferType(n.els);
      return a === b ? a : 'unknown';
    }
  }
}

/** Řádky porovnávací funkce — jeden klíč = jeden řádek `d = …`. */
function comparatorLines(keys: SortKey[], indent: string): string[] {
  return keys.map((k) => {
    const a = gen(k.expr, 'a', 0);
    const b = gen(k.expr, 'b', 0);
    const [x, y] = k.desc ? [b, a] : [a, b];
    const cmp = inferType(k.expr) === 'string' ? `(${x}).localeCompare(${y})` : `(${x}) - (${y})`;
    return `${indent}d = ${cmp}; if (d !== 0) return d;${k.desc ? ' // sestupně' : ''}`;
  });
}

function genFilter(expr: Node): string {
  return `function filtr(t, ctx) {\n  return (${gen(expr, 't', 0)});\n}`;
}

function genSort(keys: SortKey[]): string {
  return [
    'function porovnej(a, b, ctx) {',
    '  let d;',
    ...comparatorLines(keys, '  '),
    '  return 0;',
    '}',
  ].join('\n');
}

function genSearchBlocks(blocks: SearchBlock[], indent: string): string[] {
  const out: string[] = [];
  for (const b of blocks) {
    if (b.type === 'filter') {
      out.push(`${indent}trasy = trasy.filter((t) => ${gen(b.expr, 't', 0)});`);
    } else {
      out.push(`${indent}trasy = [...trasy].sort((a, b) => {`);
      out.push(`${indent}  let d;`);
      out.push(...comparatorLines(b.keys, `${indent}  `));
      out.push(`${indent}  return 0;`);
      out.push(`${indent}});`);
    }
  }
  return out;
}

function genSearch(prog: SearchProgram): string {
  const lines = ['function hledej(trasy, ctx) {'];
  for (const branch of prog.branches) {
    lines.push(`  if (${gen(branch.cond, 't', 0)}) {`);
    lines.push(...genSearchBlocks(branch.blocks, '    '));
    lines.push('    return trasy;');
    lines.push('  }');
  }
  lines.push(...genSearchBlocks(prog.blocks, '  '));
  lines.push('  return trasy;');
  lines.push('}');
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Veřejné API
// ---------------------------------------------------------------------------

function compileInternal(src: string, kind: ProgramKind): string {
  if (src.trim() === '') {
    throw new LangError(
      kind === 'filter' ? 'Prázdný výraz — zadejte podmínku filtru.' : 'Prázdný program — není co zkompilovat.',
      0,
    );
  }
  const p = new Parser(src);
  switch (kind) {
    case 'filter': {
      const expr = p.parseExpr();
      p.expectEof();
      return genFilter(expr);
    }
    case 'sort':
      return genSort(p.parseSortProgram());
    case 'search':
      return genSearch(p.parseSearchProgram());
  }
}

/**
 * Zkompiluje program výrazového jazyka do čitelného JavaScriptu.
 * Při chybě vyhazuje Error s českou hláškou (a pozicí v `pos`);
 * pro validaci bez výjimek použijte {@link validateExpression}.
 */
export function compileExpression(src: string, kind: ProgramKind): CompiledProgram {
  return { kind, jsSource: compileInternal(src, kind) };
}

/** Ověří program; při chybě vrací českou hlášku a pozici znaku ve zdroji. */
export function validateExpression(
  src: string,
  kind: ProgramKind,
): { ok: true } | { ok: false; error: string; pos?: number } {
  try {
    compileInternal(src, kind);
    return { ok: true };
  } catch (e) {
    if (e instanceof LangError) return { ok: false, error: e.message, pos: e.pos };
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// ---------------------------------------------------------------------------
// Referenční dokumentace
// ---------------------------------------------------------------------------

const typCz: Record<ValueType, string> = { number: 'číslo', boolean: 'ano/ne', string: 'text' };

/** Stručná česká reference jazyka pro zobrazení v editoru. */
export const LANG_DOC: string = [
  'VÝRAZOVÝ JAZYK PRO TRASY',
  '',
  'Klíčová slova nerozlišují velikost písmen, diakritika je volitelná („čekání" = „cekani").',
  '',
  'Proměnné trasy:',
  ...IDENT_DEFS.filter((d) => !d.ctx).map((d) => `  ${d.name} (${typCz[d.type]}) — ${d.doc}`),
  '',
  'Kontext odjezdu:',
  ...IDENT_DEFS.filter((d) => d.ctx).map((d) => `  ${d.name} (${typCz[d.type]}) — ${d.doc}`),
  '',
  'Operátory:',
  '  + - * / %, závorky; porovnání < <= > >= = != (= znamená rovnost)',
  '  logické: A / AND / &&, NEBO / OR / ||, NE / NOT / !',
  '  podmínka: KDYZ podmínka PAK hodnota JINAK hodnota',
  '',
  'Funkce:',
  ...FN_DEFS.map((f) => `  ${f.doc}`),
  '',
  'Čísla: desetinná s tečkou i čárkou (45.5 = 45,5). Pozor: v argumentech funkcí',
  'pište za oddělovací čárkou mezeru — „min(1,5)" znamená min(1.5).',
  'Řetězce v uvozovkách: rezim = "pt".',
  '',
  'FILTR — jeden pravdivostní výraz:',
  '  cas < 45 A cena <= 60 A NE odhad',
  '',
  'RAZENI — PODLE klíč [SESTUPNE], další klíč …  (výchozí je vzestupně):',
  '  PODLE cas + cekani*2, cena SESTUPNE',
  '',
  'HLEDANI — bloky FILTR / RAZENI oddělené středníkem nebo novým řádkem;',
  'volitelné větve KDYZ (smí používat jen hodina, den, vikend), první platná vyhraje:',
  '  KDYZ vikend: FILTR cena <= 30; RAZENI PODLE cena KONEC',
  '  FILTR cas < 45',
  '  RAZENI PODLE cas',
].join('\n');
